import { Component } from '@angular/core';
import { LiabilityDataModel } from '../../models/liability-data.model';
import { PaymentService } from '../../services/payment.service';
import { PaymentMethod } from '../../models/payment-method.model';
import { PaymentTypeEnum } from '../../models/payment-type.model';
import { ActivatedRoute, Router } from '@angular/router';
import { PaymentForm } from '../../forms/payment.form';
import { combineLatest, Observable } from 'rxjs';
import { map, startWith, tap } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import {
  BillGatewayAppDialogComponent,
  BillGatewayAppDialogData
} from '../../components/bill-gateway-app-dialog/bill-gateway-app-dialog.component';
import { LinkParamsService } from '../../services/link-params.service';
import { AvailablePaymentMethodModel } from '../../models/available-payment-method.model';
import { TransferPaymentDialogComponent } from '../../components/transfer-payment-dialog/transfer-payment-dialog.component';
import { BrandService } from '../../services/brand.service';
import { Brand } from '../../models/brand.model';
import { PaymentSummaryService } from '../../services/payment-summary.service';
import { AvailableBillGatewayAppsModel } from '../../models/available-bill-gateway-apps.model';
import { PaymentMethodUtils } from '../../utils/payment-method-utils';
import { PaymentSummary } from '../../models/payment-summary.model';
import { BillGatewayAppsService } from '../../services/bill-gateway-apps.service';

@Component({
  selector: 'app-single-payment',
  templateUrl: './single-payment.component.html',
  styleUrls: ['./single-payment.component.scss']
})
export class SinglePaymentComponent {
  payment: LiabilityDataModel;
  data$: Observable<Data>;
  data: Data;
  paymentType: PaymentTypeEnum;
  form = new PaymentForm();
  appHover = false;
  availablePaymentMethods: AvailablePaymentMethodModel[];
  availableBillGatewayApps: AvailableBillGatewayAppsModel[];
  submitReady$: Observable<boolean>;
  submitted = false;
  brand: Brand;
  valuePropositionVisible: boolean;
  imojeTosVisible$: Observable<boolean>;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private paymentService: PaymentService,
    private linkParamsService: LinkParamsService,
    private paymentSummaryService: PaymentSummaryService,
    private billGatewayAppsService: BillGatewayAppsService,
    private matSnackBar: MatSnackBar,
    private matDialog: MatDialog,
    private brandService: BrandService
  ) {
    this.brand = this.brandService.brand;
    this.payment = this.paymentService.payment;
    this.availableBillGatewayApps = this.billGatewayAppsService.getAvailableBillGatewayApps(
      this.payment.availableBillGatewayApps
    );
    this.availablePaymentMethods = this.payment.availablePaymentMethods.map(paymentMethod => ({
      paymentMethod,
      colSpan: 1
    }));

    if (this.availablePaymentMethods.length) {
      this.availablePaymentMethods[this.availablePaymentMethods.length - 1].colSpan =
        this.availablePaymentMethods.length % 2 > 0 ? 2 : 1;
    }

    this.valuePropositionVisible = !!this.availableBillGatewayApps.length;

    this.form.fill({ ...this.payment, ...this.linkParamsService.linkParams });
    const paymentType$ = this.route.data.pipe(
      startWith(this.route.snapshot.data),
      map(data => data.paymentType)
    );
    const paymentMethod$ = this.form.paymentMethod.valueChanges.pipe(startWith(this.form.paymentMethod.value));
    this.data$ = combineLatest([paymentType$, paymentMethod$]).pipe(
      map(
        ([paymentType, paymentMethod]) =>
          ({
            paymentType,
            paymentMethod,
            paymentSummary: this.paymentSummaryService.calculateSummary(paymentType, paymentMethod),
            isAppSelected: PaymentMethodUtils.isBillGatewayApp(paymentMethod)
          } as Data)
      ),
      tap(data => (this.data = data))
    );
    this.submitReady$ = this.form.valueChanges.pipe(map(_ => this.form.valid));
    this.imojeTosVisible$ = this.form.imojeTosRequired$;
  }

  private static scrollToElement(targetId: string) {
    document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth' });
  }

  onSubmit() {
    if (this.form.valid) {
      if (PaymentMethodUtils.isImojePaymentMethod(this.form.paymentMethod.value)) {
        this.startPayment();
      } else if (this.form.paymentMethod.value) {
        this.openBillGatewayAppDialog();
      }
    } else {
      this.form.markAllAsTouched();
      const targetId = !!this.form.paymentMethod.value ? 'personalDataForm' : 'paymentMethod';
      SinglePaymentComponent.scrollToElement(targetId);
      this.showFormWarning();
    }
  }

  private openBillGatewayAppDialog() {
    this.matDialog.open(BillGatewayAppDialogComponent, {
      data: {
        token: this.paymentService.token,
        billGatewayApp: this.availableBillGatewayApps.find(
          ({ code }) => code === (this.form.paymentMethod.value as string)
        ),
        biller: this.payment.biller,
        personalData: this.form.personalData.value,
        paymentType: this.data.paymentType
      } as BillGatewayAppDialogData,
      autoFocus: false,
      disableClose: true,
      maxWidth: '100%'
    });
  }

  private startPayment(): void {
    if (this.form.paymentMethod.value === PaymentMethod.SIMP) {
      this.router.navigate(['wire-transfer'], { relativeTo: this.route, queryParamsHandling: 'preserve' });
    } else if (this.form.paymentMethod.value === PaymentMethod.BLIK) {
      this.submitted = true;
      this.paymentService
        .createImojeTransaction(
          this.form.paymentMethod.value,
          this.form.personalData.name.value,
          this.form.personalData.surname.value,
          this.form.personalData.email.value,
          this.data.paymentType
        )
        .subscribe();
    } else if (this.form.paymentMethod.value === PaymentMethod.TRANSFER) {
      this.openTransferPaymentDialog();
    } else if (this.form.paymentMethod.value === PaymentMethod.CARD) {
      this.router.navigate(['card-payment'], {
        relativeTo: this.route,
        queryParams: {
          name: this.form.personalData.name.value,
          surname: this.form.personalData.surname.value,
          email: this.form.personalData.email.value,
          paymentType: this.data.paymentType === PaymentTypeEnum.BALANCE ? 'balance' : 'liability'
        }
      });
    } else {
      this.submitted = true;
      this.paymentService
        .createImojePayment(
          this.form.paymentMethod.value as PaymentMethod,
          this.form.personalData.name.value,
          this.form.personalData.surname.value,
          this.form.personalData.email.value,
          this.data.paymentType
        )
        .subscribe();
    }
  }

  private openTransferPaymentDialog() {
    this.matDialog.open(TransferPaymentDialogComponent, {
      width: '1000px',
      maxWidth: '100vw',
      disableClose: true,
      data: {
        personalData: this.form.personalData.value,
        paymentType: this.data.paymentType
      }
    });
  }

  private showFormWarning() {
    let messages = [];
    if (this.form.paymentMethod.invalid) {
      messages.push('wybierz metodę płatności');
    }

    if (this.form.personalData.invalid) {
      messages.push('wypełnij formularz danych osobowych');
    }

    if (messages.length >= 2) {
      messages = [
        [...messages].splice(0, messages.length - 1).join(', '),
        ' oraz ',
        ...[...messages].splice(messages.length - 1, 1)
      ];
    }

    this.matSnackBar.open(`Aby kontynuować płatność, ${messages.join('')}.`, null, {
      verticalPosition: 'top',
      horizontalPosition: 'center',
      duration: 5000,
      panelClass: 'snackbar-warning'
    });
  }
}

interface Data {
  paymentType: PaymentTypeEnum;
  paymentMethod: PaymentMethod | string;
  paymentSummary: PaymentSummary;
  isAppSelected: boolean;
}
